import {
  AutocompleteLocation,
  Country,
  Location,
  RegionObject,
} from "../types/location.types";

export class FormatService {
  constructor() {}

  /**
   * deriveRegions
   * @description Get unique regions from an array of locations
   * @param {Location[]} locations  Array of locations
   * @returns {string[]}            Array of region labels
   */
  public deriveRegions(locations: Location[]): string[] {
    const regions = new Set<Location["region"]>();

    locations.forEach((location) => {
      regions.add(location.region);
    });

    return Array.from(regions);
  }

  /**
   * deriveCountries
   * @description Derive countries from an array of locations
   * @param {Location[]} locations  Array of locations
   * @returns {Country[]}           Array of countries
   */
  public deriveCountries(locations: Location[]): Country[] {
    const countries = new Set<Country["label"]>();

    locations.forEach((location) => countries.add(location.country));

    const unsorted: Country[] = Array.from(countries).map((country) => {
      const locationsForCountry = locations.filter(
        (location) => location.country === country
      );

      return {
        score: this.calculateScore(locationsForCountry),
        label: country,
        labelAlt: locationsForCountry.find(
          (_country) => _country.country === country
        )?.countryAlt,
        unlocode: locationsForCountry[0].unlocode.slice(0, 2),
        locations: locationsForCountry,
      };
    });

    const sorted = unsorted.sort((a, b) => b.score - a.score);
    return sorted;
  }

  /**
   * sortCountriesToRegions
   * @description Assign countries to their corresponding regions
   * @param {string[]} regions      Array of regions
   * @param {Location[]} locations  Array of locations
   * @returns {RegionObject}        Object with regions as keys
   */
  sortCountriesToRegions(
    regions: string[],
    locations: Location[]
  ): RegionObject {
    const objectWithRegions: RegionObject = {};

    regions.forEach((region) => {
      const locationsForRegion = locations.filter(
        (location) => location.region === region
      );

      objectWithRegions[region] = {
        score: this.calculateScore(locationsForRegion),
        countries: this.deriveCountries(locationsForRegion),
        label: region,
        labelAlt: locationsForRegion.find(
          (_region) => _region.region === region
        )?.regionAlt,
      };

      delete objectWithRegions[region].locations;
    });

    return objectWithRegions;
  }

  /**
   * convertObjectToArray
   * @description Convert a RegionObject to a sorted array of regions, countries and locations sorted by score
   * @param {RegionObject} regionObject   Object with regions as keys
   * @returns {AutocompleteLocation[]}    Array of regions, countries and locations sorted by score
   */
  convertObjectToArray(
    regionObject: RegionObject
  ): Array<AutocompleteLocation> {
    const sortedArray: AutocompleteLocation[] = [];

    const sorted = Array.from(Object.values(regionObject)).sort(
      (a, b) => b.score - a.score
    );

    sorted.forEach((region) => {
      // Add Region to Array
      const regionUnlocodes = region.countries.map(
        (country) => country.unlocode
      );

      sortedArray.push({
        unlocode: regionUnlocodes.join(","),
        label: region.label,
        searchLabel: region.label,
        searchLabelAlt: region.labelAlt,
        region: region.label,
        score: region.score,
      });

      // Add Country to Array
      region.countries.forEach((country) => {
        sortedArray.push({
          unlocode: country.unlocode,
          label: `${region.label}, ${country.label}`,
          searchLabel: `${region.label} ${country.label}`,
          searchLabelAlt: `${region.labelAlt} ${country.labelAlt}`,
          chipLabel: country.label,
          region: region.label,
          country: country.label,
          score: country.score,
        });

        // Add Locations to Array
        country.locations.forEach((location) => {
          sortedArray.push({
            unlocode: location.unlocode,
            label: `${region.label}, ${location.label}`,
            searchLabel: `${region.label} ${country.label} ${location.label}`,
            searchLabelAlt: `${region.labelAlt} ${country.labelAlt} ${location.labelAlt}`,
            chipLabel: location.label,
            region: region.label,
            country: country.label,
            score: location.score,
          });
        });
      });
    });

    return sortedArray;
  }

  /**
   * calculateScore
   * @description Calculate the score of a Region / Country based on the score of its locations
   * @param {Location} locations  Locations to calculate the score from
   * @returns {number}            Cumulated score of the region / country
   */
  private calculateScore(locations: Location[]): number {
    return locations.reduce((acc: number, curr: Location) => {
      return acc + curr.score;
    }, 0);
  }
}
